/**
 * Head generator — the static replacement for the WP mu-plugin's head
 * injection: meta, Open Graph, Schema.org JSON-LD, and the page stylesheet.
 */
import { escapeHtml } from './escape.js';
import type { PageT, SiteContentT } from '../schema/index.js';

export interface HeadOptions {
  /** Site-wide stylesheet contents, inlined into <style>. */
  baseCss?: string;
  /** Extra trusted head markup from the pipeline (e.g. font links). */
  extraHead?: string;
}

function canonicalUrl(site: SiteContentT['site'], page: PageT): string | undefined {
  if (!site.domain) return undefined;
  const path = page.path === '/' ? '/' : `${page.path}/`;
  return `https://${site.domain}${path}`;
}

/** Schema.org LocalBusiness JSON-LD for the site. */
export function jsonLd(content: SiteContentT): string {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: content.site.name,
  };
  if (content.site.domain) data.url = `https://${content.site.domain}/`;
  if (content.site.tagline) data.description = content.site.tagline;
  // Script-safe serialization: "</script>" or "<!--" inside content values
  // must never terminate the JSON-LD script block (stored-XSS vector).
  return JSON.stringify(data).replaceAll('<', String.raw`\u003c`);
}

export function renderHead(content: SiteContentT, page: PageT, sectionCss: string, options: HeadOptions = {}): string {
  const { site } = content;
  const url = canonicalUrl(site, page);
  const description = page.metaDescription ?? site.tagline;

  const parts: string[] = [
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(page.title)}</title>`,
  ];
  if (description) parts.push(`<meta name="description" content="${escapeHtml(description)}">`);
  if (url) parts.push(`<link rel="canonical" href="${escapeHtml(url)}">`);
  parts.push(`<meta property="og:title" content="${escapeHtml(page.title)}">`);
  if (description) parts.push(`<meta property="og:description" content="${escapeHtml(description)}">`);
  parts.push('<meta property="og:type" content="website">');
  if (url) parts.push(`<meta property="og:url" content="${escapeHtml(url)}">`);
  parts.push(`<script type="application/ld+json">${jsonLd(content)}</script>`);
  if (options.extraHead) parts.push(options.extraHead);

  const css = [options.baseCss, sectionCss].filter(Boolean).join('\n');
  if (css) parts.push(`<style>${css}</style>`);

  return parts.join('\n');
}
